import { useState, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckSquare,
  Square,
  Radio,
  Clock,
  MessageSquare,
  User,
  Loader2,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { projectsApi, getApiErrorMessage } from '@/services/api'
import type { Project } from '@/types'

// ---------------------------------------------------------------
// Types
// ---------------------------------------------------------------

interface ChecklistItem {
  id: string
  label: string
  done: boolean
  category: string
}

interface LiveUpdate {
  id: string
  timestamp: string
  message: string
  userId?: string
  type: 'info' | 'alert' | 'success'
  author?: string
}

interface TeamMember {
  id?: string
  name: string
  role?: string
  status?: 'filming' | 'shooting' | 'standby' | 'break'
  location?: string
  initial?: string
  color?: string
}

interface ControlRoomData {
  projectId: string
  name: string
  eventStatus: string | null
  startDate: string | null
  endDate?: string | null
  location?: string | null
  isOnline: boolean
  client?: { id: string; name: string; slug: string; logo?: string }
  checklist: ChecklistItem[]
  checklistProgress: { total: number; done: number; percentage: number }
  eventTeam: TeamMember[]
  eventUpdates: LiveUpdate[]
  budget?: number | null
  spentBudget: number
}

// ---------------------------------------------------------------
// Style maps
// ---------------------------------------------------------------

const TEAM_STATUS_COLOR: Record<string, string> = {
  filming: 'bg-danger-50 text-danger-600',
  shooting: 'bg-warning-50 text-warning-600',
  standby: 'bg-success-50 text-success-600',
  break: 'bg-slate-100 text-slate-500',
}

const TEAM_STATUS_LABEL: Record<string, string> = {
  filming: 'Ripresa',
  shooting: 'Foto',
  standby: 'Standby',
  break: 'Pausa',
}

const AVATAR_COLORS = [
  'bg-red-500', 'bg-blue-500', 'bg-emerald-500', 'bg-violet-500',
  'bg-orange-500', 'bg-pink-500', 'bg-cyan-500', 'bg-amber-500',
]

const EVENT_STATUS_NEXT: Record<string, string> = {
  PLANNING: 'PRE_EVENT',
  PRE_EVENT: 'LIVE',
  LIVE: 'POST_EVENT',
  POST_EVENT: 'COMPLETED',
}

const EVENT_STATUS_LABEL: Record<string, string> = {
  PLANNING: 'Pianificazione',
  PRE_EVENT: 'Pre-Evento',
  LIVE: 'Live',
  POST_EVENT: 'Post-Evento',
  COMPLETED: 'Completato',
}

const UPDATE_STYLE = {
  info: 'border-slate-200 bg-slate-50',
  alert: 'border-warning-200 bg-warning-50',
  success: 'border-success-200 bg-success-50',
}

const UPDATE_TEXT_COLOR = {
  info: 'text-slate-700',
  alert: 'text-warning-700',
  success: 'text-success-700',
}

const EUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

// ---------------------------------------------------------------
// ControlRoomTab component
// ---------------------------------------------------------------

export function ControlRoomTab({ project }: { project: Project }) {
  const queryClient = useQueryClient()

  const [checklist, setChecklist] = useState<ChecklistItem[]>([])
  const [newUpdateText, setNewUpdateText] = useState('')
  const [updateType, setUpdateType] = useState<'info' | 'alert' | 'success'>('info')
  const checklistDirty = useRef(false)
  const [error, setError] = useState<string | null>(null)

  // ---------------------------------------------------------------
  // Live clock
  // ---------------------------------------------------------------
  const [timeStr, setTimeStr] = useState(() =>
    new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  )
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeStr(new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }))
    }, 30_000)
    return () => clearInterval(interval)
  }, [])

  // ---------------------------------------------------------------
  // Fetch control room data
  // ---------------------------------------------------------------
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['project-control-room', project.id],
    queryFn: async () => {
      const res = await projectsApi.getControlRoom(project.id)
      return res.data as ControlRoomData
    },
    enabled: Boolean(project.id),
    refetchInterval: 30_000,
  })

  const data: ControlRoomData | undefined = rawData

  // Hydrate checklist from fetched data (only when not locally dirty)
  useEffect(() => {
    if (data && !checklistDirty.current) {
      const rawChecklist = Array.isArray(data.checklist) ? data.checklist : []
      const items: ChecklistItem[] = rawChecklist.map((item, i) => ({
        id: (item as ChecklistItem).id ?? `item-${i}`,
        label: (item as ChecklistItem).label ?? String(item),
        done: Boolean((item as ChecklistItem).done),
        category: (item as ChecklistItem).category ?? 'Generale',
      }))
      setChecklist(items)
    }
  }, [data])

  // ---------------------------------------------------------------
  // Checklist toggle + sync
  // ---------------------------------------------------------------
  const checklistMutation = useMutation({
    mutationFn: (items: ChecklistItem[]) => projectsApi.updateChecklist(project.id, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-control-room', project.id] })
    },
    onError: (err) => setError(getApiErrorMessage(err)),
  })

  function toggleItem(itemId: string) {
    checklistDirty.current = true
    const next = checklist.map((item) =>
      item.id === itemId ? { ...item, done: !item.done } : item
    )
    setChecklist(next)
    checklistMutation.mutate(next)
  }

  const completedCount = checklist.filter((i) => i.done).length
  const categories = Array.from(new Set(checklist.map((i) => i.category)))

  // ---------------------------------------------------------------
  // Status change
  // ---------------------------------------------------------------
  const statusMutation = useMutation({
    mutationFn: (newStatus: string) => projectsApi.updateEventStatus(project.id, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-control-room', project.id] })
      queryClient.invalidateQueries({ queryKey: ['project', project.id] })
    },
    onError: (err) => setError(getApiErrorMessage(err)),
  })

  // ---------------------------------------------------------------
  // Add update
  // ---------------------------------------------------------------
  const updateMutation = useMutation({
    mutationFn: (payload: { message: string; type: string }) =>
      projectsApi.addEventUpdate(project.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-control-room', project.id] })
      setNewUpdateText('')
    },
    onError: (err) => setError(getApiErrorMessage(err)),
  })

  function handleSendUpdate() {
    if (!newUpdateText.trim()) return
    updateMutation.mutate({ message: newUpdateText.trim(), type: updateType })
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendUpdate()
    }
  }

  // ---------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------
  const eventStatus = data?.eventStatus ?? ''
  const location = data?.location ?? (data?.isOnline ? 'Online' : '')

  const startDate = data?.startDate ? new Date(data.startDate) : null
  const endDate = data?.endDate ? new Date(data.endDate) : null

  let currentDay = 1
  let totalDays = 1
  if (startDate && endDate) {
    const msPerDay = 1000 * 60 * 60 * 24
    totalDays = Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / msPerDay) + 1)
    const now = new Date()
    currentDay = Math.max(1, Math.min(totalDays, Math.round((now.getTime() - startDate.getTime()) / msPerDay) + 1))
  }

  const teamMembers: TeamMember[] = Array.isArray(data?.eventTeam) ? data!.eventTeam : []
  const updates: LiveUpdate[] = Array.isArray(data?.eventUpdates)
    ? [...data!.eventUpdates].reverse()
    : []

  const nextStatus = eventStatus ? EVENT_STATUS_NEXT[eventStatus] : undefined

  // ---------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    )
  }

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Error banner */}
      {error && (
        <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline text-xs">Chiudi</button>
        </div>
      )}

      {/* Header bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 p-4 bg-white rounded-2xl border border-slate-200 shadow-card">
        <div className="flex items-center gap-3">
          {/* Status badge */}
          {eventStatus === 'LIVE' ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-danger-50 rounded-full">
              <Radio className="w-3.5 h-3.5 text-danger-600 animate-pulse" />
              <span className="text-xs font-semibold text-danger-600 uppercase tracking-wide">Live</span>
            </div>
          ) : eventStatus ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
              <span className="text-xs font-semibold text-slate-600">{EVENT_STATUS_LABEL[eventStatus] ?? eventStatus}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
              <span className="text-xs font-semibold text-slate-400">Nessuno stato evento</span>
            </div>
          )}

          {/* Status transition button */}
          {nextStatus && (
            <button
              onClick={() => statusMutation.mutate(nextStatus)}
              disabled={statusMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              {statusMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
              Avanza a {EVENT_STATUS_LABEL[nextStatus] ?? nextStatus}
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Day counter */}
          {totalDays > 1 && (
            <div className="flex items-center gap-1.5 text-sm text-slate-500">
              <span>Giorno</span>
              <span className="font-semibold text-slate-800 tabular-nums">{currentDay}</span>
              <span className="tabular-nums">di {totalDays}</span>
            </div>
          )}

          {/* Clock */}
          <div className="flex items-center gap-1.5 text-slate-400">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-sm tabular-nums">{timeStr}</span>
          </div>
        </div>
      </div>

      {/* Main 3-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── LEFT: Checklist ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-4 flex flex-col max-h-[600px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Checklist</h3>
            <span className="text-xs text-slate-400 tabular-nums">
              {completedCount}/{checklist.length}
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
            <div
              className="h-full bg-success-500 rounded-full transition-all duration-300"
              style={{ width: `${checklist.length > 0 ? (completedCount / checklist.length) * 100 : 0}%` }}
            />
          </div>

          {checklist.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <p className="text-xs text-slate-400 text-center">
                Nessun elemento in checklist.
              </p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-4">
              {categories.map((cat) => (
                <div key={cat}>
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    {cat}
                  </p>
                  <div className="space-y-1">
                    {checklist
                      .filter((i) => i.category === cat)
                      .map((item) => (
                        <button
                          key={item.id}
                          onClick={() => toggleItem(item.id)}
                          disabled={checklistMutation.isPending}
                          className={cn(
                            'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors',
                            item.done
                              ? 'bg-slate-50 text-slate-400'
                              : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                          )}
                        >
                          {item.done ? (
                            <CheckSquare className="w-3.5 h-3.5 text-success-600 flex-shrink-0" />
                          ) : (
                            <Square className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                          )}
                          <span className={cn('text-xs', item.done && 'line-through')}>
                            {item.label}
                          </span>
                        </button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── CENTER: Live updates ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-4 flex flex-col max-h-[600px]">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Aggiornamenti Live
          </h3>

          {/* Updates feed */}
          <div className="flex-1 overflow-y-auto space-y-2 mb-3">
            {updates.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-xs text-slate-400">Nessun aggiornamento ancora.</p>
              </div>
            ) : (
              updates.map((update, idx) => {
                const type = (['info', 'alert', 'success'].includes(update.type) ? update.type : 'info') as 'info' | 'alert' | 'success'
                const time = update.timestamp
                  ? new Date(update.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
                  : ''
                const author = update.author ?? update.userId ?? 'Sistema'

                return (
                  <div
                    key={update.id ?? idx}
                    className={cn('rounded-lg border p-3', UPDATE_STYLE[type])}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="w-3 h-3 text-slate-500" />
                        </div>
                        <span className="text-xs font-medium text-slate-600">{author}</span>
                      </div>
                      <span className="text-[10px] tabular-nums text-slate-400">{time}</span>
                    </div>
                    <p className={cn('text-xs leading-relaxed', UPDATE_TEXT_COLOR[type])}>
                      {update.message}
                    </p>
                  </div>
                )
              })
            )}
          </div>

          {/* New update input */}
          <div className="space-y-2 border-t border-slate-200 pt-3">
            <div className="flex items-center gap-1.5">
              {(['info', 'success', 'alert'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setUpdateType(t)}
                  className={cn(
                    'px-2.5 py-1 text-[10px] font-medium rounded-full transition-colors',
                    updateType === t
                      ? t === 'info' ? 'bg-slate-200 text-slate-700'
                        : t === 'success' ? 'bg-success-50 text-success-600'
                        : 'bg-warning-50 text-warning-600'
                      : 'bg-white border border-slate-200 text-slate-400 hover:bg-slate-50'
                  )}
                >
                  {t === 'info' ? 'Info' : t === 'success' ? 'Successo' : 'Attenzione'}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <div className="flex-1 relative">
                <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Aggiungi aggiornamento..."
                  value={newUpdateText}
                  onChange={(e) => setNewUpdateText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-8 pr-3 py-2 text-xs bg-white border border-slate-200 rounded-lg text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                />
              </div>
              <button
                onClick={handleSendUpdate}
                disabled={!newUpdateText.trim() || updateMutation.isPending}
                className="px-3 py-2 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors flex items-center gap-1.5"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  'Invia'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Team on-site ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-4 flex flex-col max-h-[600px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Team On-Site</h3>
            <span className="text-xs text-slate-400 tabular-nums">{teamMembers.length} persone</span>
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 mb-3">
            {teamMembers.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-xs text-slate-400 text-center">
                  Nessun membro del team assegnato.
                </p>
              </div>
            ) : (
              teamMembers.map((member, idx) => {
                const nameInitials = member.name
                  ? member.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
                  : (member.initial ?? '?')
                const avatarColor = member.color ?? AVATAR_COLORS[idx % AVATAR_COLORS.length]
                const memberStatus = member.status ?? 'standby'

                return (
                  <div
                    key={member.id ?? idx}
                    className="bg-slate-50 rounded-xl p-3 flex items-center gap-3"
                  >
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white',
                        avatarColor
                      )}
                    >
                      {nameInitials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{member.name}</p>
                      {member.role && (
                        <p className="text-[10px] text-slate-400">{member.role}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {TEAM_STATUS_COLOR[memberStatus] && (
                        <span
                          className={cn(
                            'px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide',
                            TEAM_STATUS_COLOR[memberStatus]
                          )}
                        >
                          {TEAM_STATUS_LABEL[memberStatus] ?? memberStatus}
                        </span>
                      )}
                      {member.location && (
                        <span className="text-[9px] text-slate-400">{member.location}</span>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Location / info card */}
          <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 space-y-2">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Location</p>
              <p className="text-sm font-medium text-slate-700">
                {location || 'N/D'}
              </p>
            </div>

            {data?.budget !== null && data?.budget !== undefined && (
              <div className="pt-2 border-t border-slate-200">
                <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Budget</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-600 tabular-nums">
                    {EUR.format(data.budget)}
                  </span>
                  {data.spentBudget > 0 && (
                    <span className="text-[10px] text-slate-400 tabular-nums">
                      Speso: {EUR.format(data.spentBudget)}
                    </span>
                  )}
                </div>
                {data.budget > 0 && (
                  <div className="mt-1.5 h-1 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        data.spentBudget / data.budget > 0.9 ? 'bg-danger-500' : 'bg-success-500'
                      )}
                      style={{ width: `${Math.min(100, (data.spentBudget / data.budget) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
